/**
 * Invitation Notification Service
 * 
 * Manages user invitation notifications and checks for pending invitations
 * after login or signup. Provides a centralized way to handle invitation
 * acceptance flow.
 * 
 * @version 1.0.0
 */

import { Injectable, inject } from '@angular/core'
import { LoggerService } from '@core/services/infra/logger.service';;
import { Firestore, collection, query, where, getDocs } from '@angular/fire/firestore';
import { BehaviorSubject } from 'rxjs';
import { WorkspaceInvitation } from '@core/services/auth/workspace-management.service';
import { AuthStore } from '@core/services/auth/auth.store';

@Injectable({
  providedIn: 'root'
})
export class InvitationNotificationService {
  private readonly CONTEXT = 'InvitationNotificationService';

  constructor(private logger: LoggerService) {}

  private firestore = inject(Firestore);
  private authStore = inject(AuthStore);
  
  // Track if we've already checked for invitations this session
  private hasCheckedInvitations = false;
  
  // Observable for pending invitations
  private pendingInvitationsSubject = new BehaviorSubject<WorkspaceInvitation[]>([]);
  public pendingInvitations$ = this.pendingInvitationsSubject.asObservable();
  
  /**
   * Check for pending invitations for the current user
   * Should be called after login or signup
   * @param forceRefresh - Force check even if already checked this session
   */
  async checkForPendingInvitations(forceRefresh = false): Promise<WorkspaceInvitation[]> {
    // Only check once per session unless forced
    if (this.hasCheckedInvitations && !forceRefresh) {
      this.logger.debug('📬 InvitationNotificationService: Using cached invitations', this.CONTEXT);
      return this.pendingInvitationsSubject.value;
    }
    
    const user = this.authStore.user();
    if (!user?.userUID || !user?.email) {
      this.logger.debug('👤 InvitationNotificationService: No authenticated user found', this.CONTEXT);
      return [];
    }
    
    this.logger.debug('📬 InvitationNotificationService: Checking for pending invitations for', this.CONTEXT, user.email);
    
    try {
      // Query invitations by email (for users who don't have UID set yet)
      const invitationsRef = collection(this.firestore, 'workspace_invitations');
      const q = query(
        invitationsRef,
        where('invitedEmail', '==', user.email),
        where('status', '==', 'pending')
      );
      
      const snapshot = await getDocs(q);
      const invitations = snapshot.docs.map(doc => doc.data() as WorkspaceInvitation);
      
      // Filter out expired invitations
      const now = new Date();
      const validInvitations = invitations.filter(inv => {
        const raw = inv.expiresAt;
        const expiresAt = raw instanceof Date
          ? raw
          : (raw && typeof raw === 'object' && 'toDate' in raw)
            ? (raw as { toDate: () => Date }).toDate()
            : new Date(raw as unknown as string | number);
        return expiresAt > now;
      });
      
      this.logger.debug(`📬 InvitationNotificationService: Found ${validInvitations.length} pending invitations`, this.CONTEXT);
      
      this.pendingInvitationsSubject.next(validInvitations);
      this.hasCheckedInvitations = true;
      
      return validInvitations;
    } catch (error) {
      this.logger.error('❌ InvitationNotificationService: Error checking invitations:', this.CONTEXT, error);
      return [];
    }
  }
  
  /**
   * Reset the check flag (useful for testing or forcing a re-check)
   */
  resetCheckFlag(): void {
    this.hasCheckedInvitations = false;
  }
  
  /**
   * Clear all pending invitations from memory
   */
  clearPendingInvitations(): void {
    this.pendingInvitationsSubject.next([]);
    this.hasCheckedInvitations = false;
  }
  
  /**
   * Get the current count of pending invitations
   */
  getPendingInvitationCount(): number {
    return this.pendingInvitationsSubject.value.length;
  }
  
  /**
   * Check if there are any pending invitations
   */
  hasPendingInvitations(): boolean {
    return this.pendingInvitationsSubject.value.length > 0;
  }
}

